/* eslint-disable jsx-a11y/alt-text */
import React, { PureComponent, createRef, forwardRef } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { find, uniqBy, isEmpty } from 'lodash';
import i18n from 'i18n';
import requester from 'utils/requester';
import api from 'config/api';
import VIDEO_EVENTS from 'constants/video-events';
import PLAYER_TYPES from 'constants/player-types';
import AbstractPlayerInterface from '../HTML5PlayerInterface';
import VideoComponent from '../VideoComponent';
import urlHelper from 'helpers/urlhelper';
import Platform from 'platform';

const DRM_ERRORS_BY_CODE = {
  1: 'Unknown error',
  2: 'Cannot process request',
  3: 'Unknown MIME type',
  4: 'User Consent Needed',
};

const getDrmMessage = (verimatrixId) => {
  const licenseServerUrl = `https://drm.ott.volia.com:8062/PlayReady/rightsmanager.asmx?deviceid=${verimatrixId}`;
  return (
    '<?xml version="1.0" encoding="utf-8"?>' +
    '<PlayReadyInitiator xmlns="http://schemas.microsoft.com/DRM/2007/03/protocols/">' +
    '<LicenseServerUriOverride>' +
    '<LA_URL>' +
    licenseServerUrl +
    '</LA_URL>' +
    '</LicenseServerUriOverride>' +
    '</PlayReadyInitiator>'
  );
};

class HisensePlayer extends PureComponent {
  videoRef = createRef();
  drmPluginRef = createRef();
  abstractPlayerInterfaceRef = this.props.forwardedRef || createRef();
  tickerTimerID = null;
  deviceUID = Platform.getUID();

  // STVWL-615 videoTagKey was created to make video tag render only after the new manifest was loaded
  state = {
    videoTagKey: this.props.contentId,
    contentUrl: null,
    settings: [],
    drmProfile: null,
    isDrmInitialized: false,
  };

  componentDidMount() {
    this.abstractPlayerInterfaceRef.current = new AbstractPlayerInterface(this.videoRef);
    this.onPlayerOpen();
  }

  componentWillUnmount() {
    this.abstractPlayerInterfaceRef.current.stop();
    this.abstractPlayerInterfaceRef.current = null;
  }

  componentDidUpdate(prevProps, prevState) {
    // Component receives new contentUrl when audio/video track has changed
    const isNewContentUrl = prevState.contentUrl !== this.state.contentUrl;
    const isNewTimeshiftBegin = prevProps.timeshiftBegin !== this.props.timeshiftBegin;

    const isNewContentId = prevProps.contentId !== this.props.contentId;
    const isNewLang = prevProps.lang !== this.props.lang;
    const isNewQuality = prevProps.quality !== this.props.quality;

    if (isNewContentUrl) this.loadCurrentContentUrl();
    if (isNewContentId || isNewTimeshiftBegin) this.onChangeContentId();
    if (isNewLang) this.onChangeLang();
    if (isNewQuality) this.onChangeQuality();
  }

  onChangeContentId() {
    const player = this.abstractPlayerInterfaceRef.current;
    // Need to force stop if player gets new content
    player.stop();
    clearTimeout(this.tickerTimerID);
    this.tickerTimerID = setTimeout(() => this.onPlayerOpen(), 100);
  }

  onChangeLang() {
    const { contentUrl, settings } = this.state;
    const { lang } = this.props;
    const currentTrack = find(settings, { contentUrl }) || {};
    const newCurrentTrack = find(settings, { language: lang, quality: currentTrack.quality });

    if (newCurrentTrack) this.setState({ contentUrl: newCurrentTrack.contentUrl });
  }

  onChangeQuality() {
    const { contentUrl, settings } = this.state;
    const { quality } = this.props;
    const currentTrack = find(settings, { contentUrl }) || {};
    const newCurrentTrack = find(settings, { language: currentTrack.language, quality });

    if (newCurrentTrack) this.setState({ contentUrl: newCurrentTrack.contentUrl });
  }

  async onPlayerOpen() {
    const { contentId, lang, onPlayerOpen, onVideoEvent, playerType, timeshiftBegin } = this.props;
    // there is no event for action like "load manifest", but "waiting" is good solution for this
    onVideoEvent(VIDEO_EVENTS.WAITING);

    try {
      const data = await this.loadManifestSettings();
      // content id could be changed during async operation
      if (this.props.contentId !== contentId) return;

      const { contentSettings: settings, profile: drmProfile } = data;
      const defaultTrack = [lang, 'UA', 'RU', 'EN', 'QA']
        .map((language) => find(settings, { quality: 'auto', language }))
        .find((track) => !!track);
      let contentUrl = (defaultTrack || data).contentUrl;

      // For timeshift player src needs to be modified with a timestamp
      if (playerType === PLAYER_TYPES.TIMESHIFT && timeshiftBegin) {
        contentUrl += `?begin=${timeshiftBegin}`;
        data.contentSettings.map((setting) => (setting.contentUrl += `?begin=${timeshiftBegin}`));
      }

      if (contentUrl && [PLAYER_TYPES.CHANNEL, PLAYER_TYPES.CATCHUP, PLAYER_TYPES.TIMESHIFT].includes(playerType)) {
        const divider = contentUrl.includes('?') ? '&' : '?';
        contentUrl += `${divider}did=${this.deviceUID}`;
        data.contentSettings.map((setting) => (setting.contentUrl += `${divider}did=${this.deviceUID}`));
      }
      this.setState({ settings, drmProfile, contentUrl, videoTagKey: contentId });
      onPlayerOpen();
    } catch (error) {
      console.log(error);
    }
  }

  loadDrmClient() {
    const { verimatrixId } = this.props;
    const pluginElement = ReactDOM.findDOMNode(this.drmPluginRef.current);
    const DRMSystemID = 'urn:dvb:casystemid:19219';
    const xmlLicenceAcquisition = getDrmMessage(verimatrixId);
    const msgType = 'application/vnd.ms-playready.initiator+xml';

    return new Promise((resolve, reject) => {
      pluginElement.onDRMMessageResult = (_msgID, _resultMsg, resultCode) => {
        resultCode === 0 ? resolve() : reject(new Error(DRM_ERRORS_BY_CODE[resultCode]));
      };
      pluginElement.onDRMRightsError = (errorState, contentID, DRMSystemID, rightsIssuerURL) => {
        const message = [
          `drmErrorHandler errorState: ${errorState}`,
          `contentID: ${contentID}`,
          `DRMSystemID: ${DRMSystemID}`,
          `rightsIssuerURL: ${rightsIssuerURL}`,
        ].join(', ');
        reject(new Error(message));
      };
      try {
        pluginElement.sendDRMMessage(msgType, xmlLicenceAcquisition, DRMSystemID);
      } catch (error) {
        reject(error);
      }
    });
  }

  async loadManifestSettings() {
    const { playerType, contentId, isLoggedIn } = this.props;
    let urls = [];

    if ([PLAYER_TYPES.CHANNEL, PLAYER_TYPES.TIMESHIFT].includes(playerType)) {
      const endpointUrlChannel = urlHelper.getAuthorizedEndpointUrl(
        api.endpoints.channelDetailsUrl(contentId, 'dashWv'),
        isLoggedIn,
      );
      urls.push(`${api.host}${endpointUrlChannel}`);
    } else if ([PLAYER_TYPES.CATCHUP, PLAYER_TYPES.VOD_CHANNEL].includes(playerType)) {
      const endpointUrlCatchUp = urlHelper.getAuthorizedEndpointUrl(
        api.endpoints.epgDetailsUrl(contentId, 'dashWv'),
        isLoggedIn,
      );
      urls.push(`${api.host}${endpointUrlCatchUp}`);
    } else {
      const endpointUrlVOD_HLSFree = urlHelper.getAuthorizedEndpointUrl(
        api.endpoints.movieDetailsUrl(contentId, 'hlsFree'),
        isLoggedIn,
      );
      const endpointUrlVOD_DashWv = urlHelper.getAuthorizedEndpointUrl(
        api.endpoints.movieDetailsUrl(contentId, 'dashWv'),
        isLoggedIn,
      );

      const endpointUrlVOD_DashFree = urlHelper.getAuthorizedEndpointUrl(
        api.endpoints.movieDetailsUrl(contentId, 'dashFree'),
        isLoggedIn,
      );

      const drmManifestData = await requester({
        method: 'get',
        url: `${api.host}${endpointUrlVOD_DashWv}`,
      });

      if (!isEmpty(drmManifestData.contentSettings)) return drmManifestData;

      //ЕСЛИ DRM профиль пришёл пустым, то пробуем найти настройки в других профилях без DRM
      urls.push(`${api.host}${endpointUrlVOD_HLSFree}`);
      urls.push(`${api.host}${endpointUrlVOD_DashFree}`);

      const freeManifests = await Promise.all(urls.map((url) => requester({ method: 'get', url })));
      return find(freeManifests, (data) => !isEmpty(data.contentSettings)) || freeManifests[0];
    }

    const manifests = await Promise.all(urls.map((url) => requester({ method: 'get', url })));
    return find(manifests, (data) => !isEmpty(data.contentSettings)) || manifests[0];
  }

  async loadCurrentContentUrl() {
    const player = this.abstractPlayerInterfaceRef.current;
    const { playerType, startFrom, onSettingsChange } = this.props;
    const { isDrmInitialized, drmProfile } = this.state;
    const isLiveChannel = [PLAYER_TYPES.CHANNEL].includes(playerType);
    const currentTime = [PLAYER_TYPES.CHANNEL, PLAYER_TYPES.TIMESHIFT].includes(playerType)
      ? 0
      : parseInt(player.getCurrentTime(), 10);
    const startTime = currentTime || startFrom;

    if (!isDrmInitialized && drmProfile === 'dashWv') {
      await this.loadDrmClient();
      this.setState({ isDrmInitialized: true });
    }

    player.stop();
    player.load();
    if (!isLiveChannel && startTime) player.setCurrentTime(startTime);
    player.play();
    onSettingsChange(this.getAvailableSettings());
  }

  getAvailableSettings() {
    const { contentUrl, settings } = this.state;
    const currentTrack = find(settings, { contentUrl }) || {};

    const quality = [
      find(settings, { quality: 'low' }),
      find(settings, { quality: 'average' }),
      find(settings, { quality: 'high' }),
      find(settings, { quality: 'veryhigh' }),
      find(settings, { quality: 'maximum' }),
      find(settings, { quality: 'auto' }),
    ]
      .filter((track) => !!track)
      .map(({ quality }) => ({
        id: quality,
        name: i18n.t(`player.videoQualities.${quality}`),
        isActive: quality === currentTrack.quality,
      }));

    let language = uniqBy(settings, 'language').map(({ language }) => ({
      id: language,
      name: i18n.t(`player.audioTracks.${language.toLowerCase()}`),
      isActive: language === currentTrack.language,
    }));

    const UaTrackIndex = language.findIndex((track) => ['uk', 'ua'].includes(track.id?.toLowerCase()));
    language.push(...language.splice(0, UaTrackIndex));

    return { quality, language };
  }

  render() {
    const { width, height, onVideoEvent } = this.props;
    const { contentUrl, videoTagKey } = this.state;

    if (!contentUrl) return null;

    const isHlsContent = contentUrl.includes('.m3u8');

    // В Hisense плеер может попасть DASH или HLS контент от Воли.
    // HLS контент от MEGOGO будет использовать HLS.js плеер (временно)

    const mediaType = isHlsContent ? 'application/x-mpegurl' : 'application/dash+xml';

    return (
      <VideoComponent
        width={width}
        height={height}
        onEvent={onVideoEvent}
        ref={this.videoRef}
        key={videoTagKey}
        preload="none"
      >
        <source src={contentUrl} type={mediaType} />
        <object
          key={videoTagKey}
          ref={this.drmPluginRef}
          id="drmplugin"
          type="application/oipfDrmAgent"
          style={{ visibility: 'hidden' }}
        />
      </VideoComponent>
    );
  }
}

HisensePlayer.propTypes = {
  playerType: PropTypes.string.isRequired,
  contentId: PropTypes.number.isRequired,
  src: PropTypes.string.isRequired,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  onPlayerOpen: PropTypes.func.isRequired,
  onSettingsChange: PropTypes.func.isRequired,
  onVideoEvent: PropTypes.func.isRequired,
  verimatrixId: PropTypes.string,
  startFrom: PropTypes.number,
  lang: PropTypes.string,
  quality: PropTypes.string,
  forwardedRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
};

export default forwardRef((props, ref) => <HisensePlayer forwardedRef={ref} {...props} />);
